import { Component, inject, OnDestroy } from '@angular/core';
import { ModalController, ViewWillEnter } from '@ionic/angular';

// Types
import { FailedCall } from '../../../shared/types/manageCalls';
import { StorageKey } from '../../../shared/types/storage';

// Services
import { ApiService } from '../../../core/http/api.service';
import { CommonService } from '../../../core/services/common/common.service';
import { LoaderService } from '../../../core/services/loader/loader.service';
import { LoggerService } from '../../../core/services/logger/logger.service';
import { NetworkService } from '../../../core/services/network/network.service';
import { StorageService } from '../../../core/services/storage/storage.service';
import { TranslationService } from '../../../core/services/translation/translation.service';
import { WsErrorService } from '../../../core/services/ws-error/ws-error.service';

@Component({
  selector: 'app-terms-and-conditions',
  templateUrl: './terms-and-conditions.page.html',
  styleUrls: ['../../../../styles/page.preset.scss'],
  standalone: false,
})
export class TermsAndConditionsPage implements ViewWillEnter, OnDestroy {
  public termsAndConditionsLanguage: string;

  private refusedTermsAndConditions: boolean;

  public readonly translate = inject(TranslationService);
  private readonly common = inject(CommonService);
  private readonly logger = inject(LoggerService);
  public readonly wsError = inject(WsErrorService);
  private readonly api = inject(ApiService);
  private readonly loaders = inject(LoaderService);
  private readonly modalCtrl = inject(ModalController);
  private readonly network = inject(NetworkService);
  private readonly storage = inject(StorageService);

  get isBlockedApp(): boolean {
    return this.network.blockedApp;
  }

  get isInternetAvailable(): boolean {
    return this.network.connected;
  }

  get hasRefusedTermsAndConditions(): boolean {
    return this.refusedTermsAndConditions;
  }

  get TERMS_AND_CONDITIONS_TEXT(): string {
    if (this.termsAndConditionsLanguage) {
      if (this.common?.configFile?.TERMS_AND_CONDITIONS) {
        return this.common.configFile.TERMS_AND_CONDITIONS[
          this.termsAndConditionsLanguage.toUpperCase() as 'IT' | 'EN'
        ];
      }
    }
  }

  ionViewWillEnter(): void {
    this.termsAndConditionsLanguage = this.common.currentLang ?? 'en';
    this.refusedTermsAndConditions = false;

    this.wsError.removeWebServiceError();
  }

  ngOnDestroy(): void {
    this.network.removeListeners('TermsAndConditionsPage');
  }

  acceptTermsAndConditions(): void {
    void this.loaders.present().then(() => {
      this.api.appFunctionalities
        .acceptTermsAndConditions()
        .subscribe({
          next: () => {
            void this.loaders.dismiss();
            void this.modalCtrl.dismiss(null, 'ok');
          },
          error: ({ error }: FailedCall) => {
            this.loaders.dismiss();

            this.logger.consoleError('TermsAndConditionsPage', 'acceptTermsAndConditions', 'setWebServiceError');
            this.wsError.setWebServiceError(error, false, false, false);
          },
        })
        .add(() => this.loaders.dismiss());
    });
  }

  rejectTermsAndConditions(): void {
    this.refusedTermsAndConditions = true;
  }

  removeRefusedTermsAndConditions(): void {
    this.refusedTermsAndConditions = false;
  }

  changeLanguage(): void {
    const langToSet = this.termsAndConditionsLanguage;
    this.storage.setOnStorage(StorageKey.Lang, langToSet).subscribe({
      next: () => {
        this.translate.setCurrentLanguage(langToSet);
      },
    });
  }
}
